import type { LayoutElement } from "@/lib/layoutRenderer/schemas/layout.schema";
import { parseFormLayoutUnknown } from "@/lib/layoutRenderer/schemas/layout.schema";
import {
	type LayoutJsonEnvelope,
	parseLayoutJsonWire,
} from "./layout-json-envelope.schema";

/**
 * Supports:
 * - Saved editor envelope: `{ title, description, layout }`
 * - Layout-only JSON: `{ type: "VerticalLayout", elements: [...] }` (root is the layout)
 */
export function resolveLayoutFromParsedLayoutJson(
	envelope: Record<string, unknown>,
): unknown | null {
	const inner = envelope.layout;
	if (inner !== null && inner !== undefined && typeof inner === "object") {
		return inner;
	}
	if ("type" in envelope && typeof envelope.type === "string") {
		return envelope;
	}
	return null;
}

/**
 * Parses the wire `layoutJson` string from a form-definition API row into a validated
 * layout tree plus the typed envelope object (title/description).
 *
 * @returns `null` when missing, invalid JSON, or layout fails zod validation.
 */
export function parseFormDefinitionWireLayoutJson(
	raw: string | null | undefined,
): { layout: LayoutElement; envelope: LayoutJsonEnvelope } | null {
	const parsed = parseLayoutJsonWire(raw);
	if (!parsed) return null;
	return {
		layout: parsed.envelope.layout,
		envelope: parsed.envelope,
	};
}

/**
 * @deprecated Prefer {@link parseLayoutJsonWire} for typed envelope parsing.
 */
export function parseFormDefinitionWireLayoutJsonLegacy(
	raw: string | null | undefined,
): { layout: LayoutElement; envelope: Record<string, unknown> } | null {
	if (raw == null || String(raw).trim() === "") return null;
	try {
		const parsed: unknown = JSON.parse(String(raw));
		if (
			parsed === null ||
			typeof parsed !== "object" ||
			Array.isArray(parsed)
		) {
			return null;
		}
		const o = parsed as Record<string, unknown>;
		const layoutCandidate = resolveLayoutFromParsedLayoutJson(o);
		if (layoutCandidate === null || typeof layoutCandidate !== "object") {
			return null;
		}
		const validated = parseFormLayoutUnknown(layoutCandidate);
		if (!validated.ok) return null;
		return { layout: validated.layout, envelope: o };
	} catch {
		return null;
	}
}
